/**
 * Member Verification (mig 1390): why a member is in an event's sign-up list, in words an operator can act on.
 *
 * The pool lists three kinds of row (SignupPoolRow.signal):
 *  - voucher:  a sign-up voucher from this event's QR code (or the host organization's, for events without one);
 *  - promo:    joined with the event organization's promo code while the event was on (same matching as Event
 *              Performance's "New members (promo code)" column);
 *  - verified: verified on this event earlier and no longer matched by the rules (kept so it can be un-verified).
 * Times are shown in the EVENT's timezone, never the viewer's.
 */
import type { SignupPoolRow } from "../services/eventAttribution.service";

export interface AlsoMatchedEvent {
  event_id: string;
  title: string | null;
  start_time: string | null;
}

export interface SignupReason {
  /** One line under the member's name, e.g. "Promo code EXPO · signed up Sat, Oct 31, 10:22 AM EDT · checked in here". */
  text: string;
  /** Set when the member also matched another show of the same organization and nobody has confirmed which. */
  ambiguity: string | null;
}

/** "Sat, Oct 31, 10:22 AM EDT" in the event's timezone; falls back to the viewer's zone if the zone is unknown. */
export function formatEventLocalTime(
  iso: string | null | undefined,
  timeZone: string | null | undefined,
): string | null {
  if (!iso) {
    return null;
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return null;
  }
  const opts: Intl.DateTimeFormatOptions = {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  };
  try {
    return new Intl.DateTimeFormat("en-US", {
      ...opts,
      timeZone: timeZone || undefined,
    }).format(d);
  } catch {
    return new Intl.DateTimeFormat("en-US", opts).format(d);
  }
}

/** "Oct 31" for the other show in an ambiguity badge, in that show's own start date as the server sent it. */
function shortDate(iso: string | null, timeZone: string | null | undefined) {
  if (!iso) {
    return null;
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return null;
  }
  try {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      timeZone: timeZone || undefined,
    }).format(d);
  } catch {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
    }).format(d);
  }
}

const BASIS_NOTE: Record<string, string> = {
  checkin: "checked in here",
  voucher: "used this show's voucher",
  verified: "confirmed here",
};

export function describeSignupReason(row: SignupPoolRow): SignupReason {
  const when = formatEventLocalTime(row.signup_at, row.event_timezone);
  if (row.signal === "voucher") {
    return {
      text: ["Voucher from this event's QR code", when]
        .filter(Boolean)
        .join(" · "),
      ambiguity: null,
    };
  }
  if (row.signal === "verified") {
    return {
      text: "Verified earlier · no longer matches the sign-up rules",
      ambiguity: null,
    };
  }
  const parts = [
    row.matched_code ? `Promo code ${row.matched_code}` : "Promo code",
    when ? `signed up ${when}` : null,
    row.match_basis ? (BASIS_NOTE[row.match_basis] ?? null) : null,
  ];
  let ambiguity: string | null = null;
  if (row.match_basis === "unassigned") {
    const others = (row.also_matched ?? [])
      .map((e) => {
        const date = shortDate(e.start_time, row.event_timezone);
        return [e.title || "another show", date ? `(${date})` : null]
          .filter(Boolean)
          .join(" ");
      })
      .filter(Boolean);
    ambiguity = `Also matched ${others.length ? others.join(", ") : "another show"} — confirm which show`;
  }
  return { text: parts.filter(Boolean).join(" · "), ambiguity };
}
